import {
  Fragment,
  isValidElement,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import Button from '@material-ui/core/Button';
import GetAppIcon from '@material-ui/icons/GetApp';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import Table from '@material-ui/core/Table';
import TablePagination from '@material-ui/core/TablePagination';
import TextField from '@material-ui/core/TextField';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { AsyncState } from './AsyncState';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Makes the header clickable; the value used to compare rows. */
  sortValue?: (row: T) => string | number | null | undefined;
  width?: number | string;
  /** The value of the cell in a CSV export; defaults to the sort value, else the rendered text. */
  csvValue?: (row: T) => string | number | null | undefined;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  getRowKey: (row: T) => string;
  /** Accessible name of the scrollable region. */
  ariaLabel: string;
  loading?: boolean;
  error?: Error | null;
  emptyMessage?: ReactNode;
  errorPrefix?: string;
  onRowClick?: (row: T) => void;
  /** aria-label of a clickable row. */
  rowLabel?: (row: T) => string;
  dense?: boolean;
  /** Below this width the table scrolls horizontally instead of squeezing. */
  minWidth?: number;
  /** Extra content rendered under the header row area, e.g. expanded details. */
  renderRowDetail?: (row: T) => ReactNode;
  /** Key of the row to highlight (e.g. the run shown in a detail pane). */
  selectedKey?: string;
  /** Adds a search box; a row matches when this text contains the query. */
  searchText?: (row: T) => string;
  searchLabel?: string;
  /** Rows per page; omit to show every row. */
  pageSize?: number;
  className?: string;
  /** Adds an « Exporter (CSV) » button: the rows, sorted and searched, in a file named after `ariaLabel`. */
  exportable?: boolean;
}

/** The text a header or cell shows, for a CSV export. */
export function nodeText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean')
    return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (isValidElement(node)) {
    return nodeText((node.props as { children?: ReactNode }).children);
  }
  return '';
}

/** Byte order mark: Excel then reads the file as UTF-8 (built from its code: prettier would inline it). */
const BOM = String.fromCharCode(0xfeff);

/** Rows as CSV for a French spreadsheet: `;` separated, quoted when needed, with a BOM. */
export function toCsv<T>(columns: Column<T>[], rows: T[]): string {
  const cell = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[;"\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const value = (column: Column<T>, row: T) => {
    if (column.csvValue) return column.csvValue(row);
    if (column.sortValue) return column.sortValue(row);
    return nodeText(column.render(row));
  };
  const lines = [
    columns.map(c => cell(nodeText(c.header) || c.key)).join(';'),
    ...rows.map(row => columns.map(c => cell(value(c, row))).join(';')),
  ];
  return `${BOM}${lines.join('\r\n')}\r\n`;
}

/** Combining accents left by `normalize('NFD')`, built from their codes (prettier would inline them). */
const DIACRITICS = new RegExp(
  `[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`,
  'g',
);

/** `Features mergées dans develop` → `features-mergees-dans-develop-2026-10-07.csv`. */
export function csvFileName(label: string, now = new Date()): string {
  const slug = label
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${slug || 'export'}-${now.toISOString().slice(0, 10)}.csv`;
}

function download(fileName: string, csv: string) {
  const url = URL.createObjectURL(
    new Blob([csv], { type: 'text/csv;charset=utf-8' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Scrollable region that keeps a dense table usable on small screens and by keyboard. */
export function ResponsiveTable({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      role="region"
      aria-label={label}
      // Keyboard users must be able to scroll the overflowing table.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      style={{
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {children}
    </div>
  );
}

const INTERACTIVE =
  'a, button, input, select, textarea, label, [role="button"], [role="link"], [role="checkbox"]';

/**
 * True when the event belongs to the row itself, not to a link/button inside
 * a cell (which keeps its own behavior) nor to a portal (menu, dialog) rendered
 * from a cell, whose events bubble through the React tree.
 */
function isOwnRowEvent(event: MouseEvent | KeyboardEvent): boolean {
  const target = event.target as Element | null;
  const row = event.currentTarget as Element;
  if (!target || !row.contains(target)) return false;
  const interactive = target.closest(INTERACTIVE);
  return !interactive || interactive === row || !row.contains(interactive);
}

/** The one table of the portal: loading/error/empty states, sorting, clickable rows, responsive. */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  ariaLabel,
  loading,
  error,
  emptyMessage = 'Aucun élément.',
  errorPrefix,
  onRowClick,
  rowLabel,
  dense = true,
  minWidth = 640,
  renderRowDetail,
  selectedKey,
  searchText,
  searchLabel = 'Rechercher',
  pageSize,
  className,
  exportable,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!rows || !searchText || !needle) return rows;
    return rows.filter(row => searchText(row).toLowerCase().includes(needle));
  }, [rows, searchText, query]);

  const sorted = useMemo(() => {
    const column = columns.find(c => c.key === sort?.key);
    if (!filtered || !column?.sortValue || !sort) return filtered;
    const value = column.sortValue;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const x = value(a);
      const y = value(b);
      if (x === y) return 0;
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      return (x < y ? -1 : 1) * factor;
    });
  }, [filtered, columns, sort]);

  const lastPage = pageSize
    ? Math.max(0, Math.ceil((sorted?.length ?? 0) / pageSize) - 1)
    : 0;
  const currentPage = Math.min(page, lastPage);
  const visible =
    sorted && pageSize
      ? sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
      : sorted;

  const onClick = (event: MouseEvent, row: T) => {
    if (isOwnRowEvent(event)) onRowClick?.(row);
  };

  const onKey = (event: KeyboardEvent, row: T) => {
    if (!isOwnRowEvent(event)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick?.(row);
    }
  };

  const searchBox = searchText && rows && rows.length > 0 && (
    <TextField
      id={`${ariaLabel}-search`}
      size="small"
      variant="outlined"
      label={searchLabel}
      value={query}
      onChange={e => {
        setQuery(e.target.value);
        setPage(0);
      }}
      style={{ marginBottom: 12, minWidth: 240 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );

  const exportButton = exportable && sorted && sorted.length > 0 && (
    <Button
      size="small"
      startIcon={<GetAppIcon fontSize="small" />}
      onClick={() => download(csvFileName(ariaLabel), toCsv(columns, sorted))}
      style={{ marginLeft: 'auto' }}
    >
      Exporter (CSV)
    </Button>
  );

  return (
    <div className={className}>
      {(searchBox || exportButton) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {searchBox}
          {exportButton}
        </div>
      )}
      <AsyncState
        loading={loading || (!rows && !error)}
        error={error}
        empty={sorted?.length === 0}
        emptyMessage={
          rows && rows.length > 0
            ? 'Aucun résultat pour cette recherche.'
            : emptyMessage
        }
        errorPrefix={errorPrefix}
      >
        <ResponsiveTable label={ariaLabel}>
          <Table size={dense ? 'small' : 'medium'} style={{ minWidth }}>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    style={{ width: column.width }}
                    sortDirection={sort?.key === column.key ? sort.dir : false}
                  >
                    {column.sortValue ? (
                      <TableSortLabel
                        active={sort?.key === column.key}
                        direction={sort?.key === column.key ? sort.dir : 'asc'}
                        onClick={() =>
                          setSort(prev => ({
                            key: column.key,
                            dir:
                              prev?.key === column.key && prev.dir === 'asc'
                                ? 'desc'
                                : 'asc',
                          }))
                        }
                      >
                        {column.header}
                      </TableSortLabel>
                    ) : (
                      column.header
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(visible ?? []).map(row => (
                <Fragment key={getRowKey(row)}>
                  <TableRow
                    hover={Boolean(onRowClick)}
                    selected={selectedKey === getRowKey(row)}
                    aria-selected={
                      selectedKey === getRowKey(row) ? true : undefined
                    }
                    aria-current={
                      selectedKey === getRowKey(row) ? 'true' : undefined
                    }
                    onClick={onRowClick ? e => onClick(e, row) : undefined}
                    onKeyDown={onRowClick ? e => onKey(e, row) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    aria-label={onRowClick ? rowLabel?.(row) : undefined}
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                  >
                    {columns.map(column => (
                      <TableCell key={column.key} align={column.align}>
                        {column.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderRowDetail && (
                    <TableRow>
                      <TableCell colSpan={columns.length}>
                        {renderRowDetail(row)}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </ResponsiveTable>
        {pageSize && (sorted?.length ?? 0) > pageSize && (
          <TablePagination
            component="div"
            count={sorted?.length ?? 0}
            page={currentPage}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
            onPageChange={(_, next) => setPage(next)}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} sur ${count}`
            }
          />
        )}
      </AsyncState>
    </div>
  );
}
